import clsx from 'clsx'
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { HistoryTransactionsPayload } from '@/backend/api'
import { TitleH2 } from '@/ui/atoms/TitleH2'
import { Dropdown, SkeletonList } from '@/ui/kit'
import { EmptyList } from '@/ui/molecules'
import { useActions, useAppSelector } from '@/utils'
import style from './style.module.scss'
import { t } from '@lingui/macro'
import { InfiniteScrollPlate } from '@/ui/atoms/InfiniteScrollPlate'
import { Loader } from '@/ui/kit/Loader'
import { historyActions } from '@/redux/history/history.slice'
import { HISTORY_TRANSACTIONS_SIZE, HISTORY_TRANSACTIONS_SORTING } from '@/core/config/history'
import { DataTestIds } from '@/utils/lib/dataTestIds'
import { useActiveExchage } from '@/utils/hooks/useActiveExchange'
import { Filter, getFilterParamsFromDropdown, getHistoryListByMonth } from './helpers'
import { HistoryTransaction } from './HistoryTransaction/HistoryTransaction'

export const HistoryTransactions: FC = () => {
  const { activeExchange } = useActiveExchage()
  const isHaveAllocation = activeExchange.pages.history.transactions.extraFilter.includes('TIGR_ALLOCATION')

  const baseFilterOptions: Filter[] = [
    { label: t({ id: 'historyPage.filter.all', message: `All transactions` }), value: 'ALL' },
    { label: t({ id: 'historyPage.filter.deposit', message: `Deposit` }), value: 'DEPOSIT' },
    { label: t({ id: 'historyPage.filter.withdrawal', message: `Withdrawal` }), value: 'WITHDRAWAL' },
    { label: t({ id: 'historyPage.filter.transfer', message: `Transfer` }), value: 'TRANSFER' },
    { label: t({ id: 'historyPage.filter.commissions', message: `Saved commission` }), value: 'CASHBACK' },
  ]

  const filterOptions = useMemo<Filter[]>(() => {
    let options = [...baseFilterOptions]

    // Убираем TRANSFER, если биржа Bybit
    if (activeExchange.type === 'BYBIT_BROKER') {
      options = options.filter(option => option.value !== 'TRANSFER')
    }

    if (isHaveAllocation) {
      options.push({
        label: t({ id: 'historyPage.filter.allocations', message: `Allocations` }),
        value: 'TIGR_ALLOCATION',
      })
    }

    return options
  }, [isHaveAllocation, activeExchange.type])

  const {
    historyData,
    historyDataStatus: historyStatus,
    historyDataReachedEnd,
  } = useAppSelector(state => state.history)

  const { GetHistoryTC } = useActions(historyActions)
  const [filter, setFilter] = useState<Filter>(filterOptions[0])

  const getHistory = useCallback(
    (page: number, size = HISTORY_TRANSACTIONS_SIZE, filterParams: HistoryTransactionsPayload['filterParams'] = {}) => {
      GetHistoryTC({
        page: page,
        size: size,
        sortParams: HISTORY_TRANSACTIONS_SORTING,
        filterParams,
      })
    },
    [GetHistoryTC]
  )

  const scrollCallback = useCallback<IntersectionObserverCallback>(
    ([entry]) => {
      if (historyDataReachedEnd) return
      if (historyStatus !== 'loading' && historyStatus !== 'failed' && entry.isIntersecting) {
        getHistory(historyData.page + 1, undefined, getFilterParamsFromDropdown(filter))
      }
    },
    [historyStatus, historyData.page, filter, historyDataReachedEnd]
  )

  useEffect(() => {
    getHistory(0, undefined, getFilterParamsFromDropdown(filter))
  }, [filter])

  const historyListByMonth = getHistoryListByMonth(historyData?.history)

  // to DO P2-605

  return (
    <div className={style.historyPage}>
      <div className={style.contentWrap}>
        <div className={style.top}>
          <TitleH2 label={t({ id: 'historyPage.title', comment: 'History' })} />

          <div className={clsx({ [style.animatable]: historyStatus === 'loading' })}>
            <Dropdown<Filter['value']>
              setSelectedOption={setFilter}
              selectedOption={filter}
              className={style.dropdown}
              options={filterOptions}
              dataTestId={DataTestIds.DropDownFilter}
            />
          </div>
        </div>

        <div className={style.content}>
          {historyStatus === 'loading' && historyData.history.length === 0 ? (
            <SkeletonList />
          ) : (
            <div className={style.listWrap}>
              {historyListByMonth.map(({ month, list }) => (
                <Fragment key={month}>
                  <div className={style.listDate}>{month}</div>
                  {list.map(item => (
                    <HistoryTransaction history={item} key={item.id} />
                  ))}
                </Fragment>
              ))}

              <InfiniteScrollPlate className={style.infiniteScrollPlate} scrollCallback={scrollCallback} />
              {historyStatus === 'loading' && historyData.history.length > 0 && <Loader />}

              {!historyListByMonth.length && (
                <EmptyList
                  label={t({
                    id: 'historyPage.empty',
                    message: 'Your history will be displayed here',
                  })}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
